import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { loginSchema, type LoginInput } from '@shared/schemas/auth'
import { useAuth } from '@/lib/auth'
import { ApiError } from '@/lib/api'

export function LoginPage() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const navigate = useNavigate()
  const [formError, setFormError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) })

  const onSubmit = async (input: LoginInput) => {
    setFormError(null)
    try {
      await login(input)
      navigate('/', { replace: true })
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t('auth.signInError'))
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-4">
      <div className="w-full max-w-sm rounded-card border border-line bg-surface p-8 shadow-sm">
        <div className="mb-8 flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-brand" aria-hidden="true" />
          <span className="font-display text-lg font-semibold tracking-wide">{t('app.name')}</span>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              className="rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-soft/60 focus:border-brand"
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-critical">{errors.email.message}</p>}
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-ink-soft">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder={t('auth.passwordPlaceholder')}
              className="rounded-md border-2 border-ink-soft/25 bg-surface-2 px-3 py-2 text-sm text-ink outline-none placeholder:text-ink-soft/60 focus:border-brand"
              {...register('password')}
            />
            {errors.password && <p className="text-xs text-critical">{errors.password.message}</p>}
          </div>

          {formError && <p className="text-sm text-critical">{formError}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-2 rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-brand-ink transition-opacity disabled:opacity-60"
          >
            {t('auth.signIn')}
          </button>
        </form>
      </div>
    </div>
  )
}
